import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Collapse,
  Drawer,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
  Avatar,
  Chip
} from '@mui/material';
import {
  Menu as MenuIcon,
  ExpandLess,
  ExpandMore,
  Category as CategoryIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getCategories } from '../api/apiClient';

const CategoriesMenu = () => {
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [imageErrors, setImageErrors] = useState(new Set());
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      // Сбрасываем ошибки изображений при открытии меню
      setImageErrors(new Set());
      
      // Загружаем категории только если они еще не загружены
      if (!categoriesLoaded) {
        fetchCategories();
      }
    }
  }, [open, categoriesLoaded]);

  const fetchCategories = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError('');
      
      const response = await getCategories();
      
      if (response.data && response.data.categories) {
        setCategories(response.data.categories);
        setCategoriesLoaded(true); // Отмечаем, что категории загружены
        console.log('✅ Категории загружены', forceRefresh ? '(принудительное обновление)' : '(кэш)');
      } else {
        setError('Не удалось загрузить категории');
      }
      
    } catch (err) {
      console.error('❌ Ошибка загрузки категорий:', err);
      setError('Ошибка загрузки категорий');
    } finally {
      setLoading(false);
    }
  };

  // Функция для принудительного обновления категорий (можно вызвать при необходимости)
  const refreshCategories = () => {
    setCategoriesLoaded(false);
    fetchCategories(true);
  };

  // Функция для проверки, является ли категория кликабельной
  const isCategoryClickable = (category) => {
    const attributes = category.attributes || {};
    
    // Некликабельные категории:
    // 1. Разделители
    if (attributes.isDivider) return false;
    
    // 2. Заголовки
    if (attributes.isTitle) return false;
    
    // 3. Переносы строк
    if (attributes.isLineBreak) return false;
    
    // 4. Категории без контента и без подкатегорий
    if (!attributes.mustDisplayContent && !category.hasSubcategories) return false;
    
    // 5. Категории, которые не должны показывать подкатегории и у них нет контента
    if (!attributes.showSubcategories && !attributes.mustDisplayContent) return false;
    
    // 6. Категории с irrelevant: true в SEO
    if (category.seo?.irrelevant) return false;
    
    return true;
  };

  // Функция для проверки, может ли категория разворачиваться
  const canCategoryExpand = (category) => {
    const attributes = category.attributes || {};
    
    // Категория может разворачиваться, если:
    // 1. У неё есть подкатегории
    // 2. И она не является разделителем, заголовком или переносом строки
    return category.hasSubcategories && 
           !attributes.isDivider && 
           !attributes.isTitle && 
           !attributes.isLineBreak;
  };

  // Функция для проверки, есть ли у категории кликабельные подкатегории
  const hasClickableSubcategories = (category) => {
    if (!category.subcategories || category.subcategories.length === 0) {
      return false;
    }
    
    return category.subcategories.some(subcategory => {
      // Рекурсивно проверяем подкатегории
      return isCategoryClickable(subcategory) || hasClickableSubcategories(subcategory);
    });
  };

  // Функция для проверки, должна ли категория отображаться в меню
  const shouldShowCategory = (category) => {
    const attributes = category.attributes || {};
    
    // Всегда показываем разделители, заголовки и переносы строк
    if (attributes.isDivider || attributes.isTitle || attributes.isLineBreak) {
      return true;
    }
    
    // Если категория кликабельная - показываем
    if (isCategoryClickable(category)) {
      return true;
    }
    
    // Если категория некликабельная, но у неё есть кликабельные подкатегории - показываем
    if (hasClickableSubcategories(category)) {
      return true;
    }
    
    // Иначе скрываем (например, "JOIN LIFE" без кликабельных подкатегорий)
    return false;
  };

  const handleCategoryClick = (category) => {
    // Если категория некликабельная, только разворачиваем/сворачиваем подкатегории
    if (!isCategoryClickable(category)) {
      // Если у категории есть подкатегории, переключаем их отображение
      if (category.hasSubcategories) {
        toggleCategoryExpansion(category.id);
      }
      return; // Не переходим на страницу категории
    }
    
    // Для кликабельных категорий переходим на страницу
    // Проверяем, есть ли перенаправление на другую категорию
    if (category.redirectCategoryId) {
      navigate(`/category/${category.redirectCategoryId}`);
    } else if (category.id) {
      navigate(`/category/${category.id}`);
    }
    setOpen(false);
  };

  const handleSubcategoryClick = (subcategory) => {
    // Если подкатегория некликабельная, только разворачиваем/сворачиваем подкатегории
    if (!isCategoryClickable(subcategory)) {
      // Если у подкатегории есть подкатегории, переключаем их отображение
      if (subcategory.hasSubcategories) {
        toggleCategoryExpansion(subcategory.id);
      }
      return; // Не переходим на страницу категории
    }
    
    // Для кликабельных подкатегорий переходим на страницу
    // Проверяем, есть ли перенаправление на другую категорию
    if (subcategory.redirectCategoryId) {
      navigate(`/category/${subcategory.redirectCategoryId}`);
    } else if (subcategory.id) {
      navigate(`/category/${subcategory.id}`);
    }
    setOpen(false);
  };

  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Функция для получения стилей категории
  const getCategoryStyles = (category) => {
    const styles = category.styles || {};
    return {
      backgroundColor: styles.backgroundColor || 'transparent',
      color: styles.color || 'inherit',
      fontFamily: styles.font || 'inherit',
      fontWeight: styles.isBold ? 'bold' : 'normal',
      fontStyle: styles.isItalics ? 'italic' : 'normal',
      textDecoration: styles.isUnderlined ? 'underline' : 'none',
      letterSpacing: styles.letterSpacing ? `${styles.letterSpacing}px` : 'normal'
    };
  };

  // Функция для рендеринга иконки или картинки категории
  const renderCategoryIcon = (category, size = 24) => {
    const styles = category.styles || {};
    const imageKey = `${category.id}-${styles.imageUrl || styles.iconUrl}`;
    const hasImageError = imageErrors.has(imageKey);
    
    // Если есть картинка категории и нет ошибки загрузки
    if (styles.imageUrl && !hasImageError) {
      return (
        <Avatar
          src={styles.imageUrl}
          alt={category.name}
          sx={{ 
            width: size, 
            height: size, 
            mr: 1,
            bgcolor: 'transparent',
            borderRadius: 1,
            '& img': {
              objectFit: 'cover',
              borderRadius: 1
            }
          }}
          onError={() => {
            setImageErrors(prev => new Set([...prev, imageKey]));
          }}
        />
      );
    }
    
    // Если есть иконка и нет ошибки загрузки
    if (styles.iconUrl && !hasImageError) {
      return (
        <Avatar
          src={styles.iconUrl}
          alt={category.name}
          sx={{ 
            width: size, 
            height: size, 
            mr: 1,
            bgcolor: 'transparent',
            borderRadius: 1,
            '& img': {
              objectFit: 'cover',
              borderRadius: 1
            }
          }}
          onError={() => {
            setImageErrors(prev => new Set([...prev, imageKey]));
          }}
        />
      );
    }
    
    // Дефолтная иконка
    return (
      <CategoryIcon 
        sx={{ 
          mr: 1, 
          fontSize: size, 
          color: 'text.secondary' 
        }} 
      />
    );
  };

  // Функция для рендеринга тега категории
  const renderCategoryTag = (category) => {
    const tag = category.styles?.tag;
    if (!tag || !tag.text) return null;
    
    return (
      <Chip
        label={tag.text}
        size="small"
        sx={{
          ml: 1,
          height: 20,
          fontSize: '0.7rem',
          color: tag.color || 'inherit',
          backgroundColor: 'rgba(0,0,0,0.1)',
          '& .MuiChip-label': {
            px: 1
          }
        }}
      />
    );
  };

  const renderCategory = (category, level = 0) => {
    const hasSubcategories = category.subcategories && category.subcategories.length > 0;
    const isExpanded = expandedCategories[category.id] || false;
    const categoryStyles = getCategoryStyles(category);
    const attributes = category.attributes || {};
    const isClickable = isCategoryClickable(category);
    const canExpand = canCategoryExpand(category);
    
    // Проверяем, является ли категория разделителем
    if (attributes.isDivider) {
      return <Divider key={category.id} sx={{ my: 1 }} />;
    }
    
    // Проверяем, является ли категория заголовком
    if (attributes.isTitle) {
      return (
        <Typography
          key={category.id}
          variant="h6"
          sx={{
            px: 2,
            py: 1,
            fontWeight: 'bold',
            color: 'text.primary',
            ...categoryStyles
          }}
        >
          {category.name}
        </Typography>
      );
    }
    
    // Проверяем, является ли категория переносом строки
    if (attributes.isLineBreak) {
      return <Box key={category.id} sx={{ height: 8 }} />;
    }
    
    return (
      <Box key={category.id}>
        <ListItem 
          disablePadding 
          sx={{ 
            pl: level * 2 + 2,
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
          }}
        >
          <ListItemButton
            onClick={() => handleCategoryClick(category)}
            sx={{ 
              minHeight: level === 0 ? 56 : 48,
              justifyContent: 'space-between',
              backgroundColor: categoryStyles.backgroundColor !== 'transparent' ? 
                categoryStyles.backgroundColor : 'transparent',
              cursor: 'pointer',
              opacity: isClickable ? 1 : 0.8,
              '&:hover': {
                backgroundColor: categoryStyles.backgroundColor !== 'transparent' ? 
                  'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.04)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <ListItemText 
                  primary={category.name} 
                  secondary={category.shortDescription}
                  primaryTypographyProps={{ 
                    fontSize: level === 0 ? '1rem' : '0.9rem',
                    fontWeight: level === 0 ? 500 : 400,
                    color: isClickable ? 'inherit' : 'text.secondary',
                    ...categoryStyles
                  }}
                  secondaryTypographyProps={{
                    fontSize: '0.75rem',
                    color: 'text.secondary'
                  }}
                />
                {renderCategoryTag(category)}
              </Box>
            </Box>
            {canExpand && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleCategoryExpansion(category.id);
                }}
                sx={{
                  opacity: 1
                }}
              >
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            )}
          </ListItemButton>
        </ListItem>
        
        {canExpand && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {category.subcategories
                .filter(subcategory => shouldShowCategory(subcategory))
                .map(subcategory => (
                  <Box key={subcategory.id}>
                    {renderCategory(subcategory, level + 1)}
                  </Box>
                ))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={() => setOpen(true)}
        sx={{ mr: 1 }}
      >
        <MenuIcon />
      </IconButton>
      
      <Drawer
        anchor="left"
        open={open}
        onClose={() => setOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 380,
            maxWidth: '85vw'
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Категории товаров
            </Typography>
            <IconButton
              size="small"
              onClick={refreshCategories}
              disabled={loading}
              sx={{ 
                opacity: 0.7,
                '&:hover': { opacity: 1 }
              }}
              title="Обновить категории"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Box>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : categories.length > 0 ? (
            <List>
              {categories
                .filter(category => shouldShowCategory(category))
                .map((category, index, filteredCategories) => (
                  <Box key={category.id || index}>
                    {renderCategory(category)}
                    {index < filteredCategories.length - 1 && !category.attributes?.isDivider && <Divider />}
                  </Box>
                ))}
            </List>
          ) : (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Категории не найдены
            </Typography>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default CategoriesMenu;
